import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  NavItem,
  NavSection,
} from '../layouts/full/vertical/sidebar/nav-item/nav-item';
import { navSections } from '../layouts/full/vertical/sidebar/sidebar-sections';

@Injectable({
  providedIn: 'root',
})
export class DynamicNavService {
  private navSectionsSubject = new BehaviorSubject<NavSection[]>(navSections);

  // Define the Affiliates nav item
  private affiliatesNavItem: NavItem = {
    displayName: 'Affiliates',
    iconName: '/assets/images/svgs/nav/affiliates.svg',
    route: 'affiliates',
  };

  private adminNavItem: NavItem = {
    displayName: 'Onboarding',
    iconName: '/assets/images/svgs/nav/dashboard.svg',
    route: '/admin',
  };

  private adminAffiliatesNavItem: NavItem = {
    displayName: 'Affiliates',
    iconName: '/assets/images/svgs/nav/affiliates.svg',
    route: '/admin/affiliates',
  };

  private firaCalculatorNavItem: NavItem = {
    displayName: 'FIRA Calculator',
    iconName: '/assets/images/svgs/nav/invoice.svg',
    route: 'fira-calculator',
  };

  constructor() {}

  /**
   * Get the current navigation sections
   */
  getNavSections(): Observable<NavSection[]> {
    return this.navSectionsSubject.asObservable();
  }

  /**
   * Get the current navigation sections value
   */
  getCurrentNavSections(): NavSection[] {
    return this.navSectionsSubject.getValue();
  }

  /**
   * Update navigation sections based on user permissions
   * @param hideDepositsForLegacyVendor Hide Deposits for legacy VBAN (pending + no bank details)
   */
  updateNavSections(
    isAffiliateManager: boolean,
    hideDepositsForLegacyVendor = false,
    isPlatformAdmin = false,
    showFiraCalculator = false,
  ): void {
    const updatedSections = this.generateNavSections(
      isAffiliateManager,
      hideDepositsForLegacyVendor,
      isPlatformAdmin,
      showFiraCalculator,
    );
    this.navSectionsSubject.next(updatedSections);
  }

  /**
   * Generate navigation sections based on user permissions
   */
  private generateNavSections(
    isAffiliateManager: boolean,
    hideDepositsForLegacyVendor = false,
    isPlatformAdmin = false,
    showFiraCalculator = false,
  ): NavSection[] {
    if (isPlatformAdmin) {
      return [
        {
          items: [this.adminNavItem, this.adminAffiliatesNavItem],
        },
      ];
    }

    // Create a deep copy of the original navigation sections
    const sections: NavSection[] = JSON.parse(JSON.stringify(navSections));

    if (hideDepositsForLegacyVendor) {
      const primarySection = sections[0];
      if (primarySection?.items?.length) {
        primarySection.items = primarySection.items.filter(
          (item) => item.route !== 'deposits',
        );
      }
    }

    const secondarySection = sections[1];
    if (secondarySection?.items) {
      if (isAffiliateManager) {
        secondarySection.items.push(this.affiliatesNavItem);
      }
      if (showFiraCalculator) {
        secondarySection.items.push(this.firaCalculatorNavItem);
      }
    }

    return sections;
  }

  /**
   * Reset navigation sections to default (without Affiliates)
   */
  resetNavSections(): void {
    this.navSectionsSubject.next(navSections);
  }
}
